import type {
  EntityOrSuggestionRef,
  PopulatedUser,
  PopulatedOrgFunction,
  PopulatedSystem,
  Artifact,
  PopulatedExternalContact,
  ProcessWithSteps,
  WhoElseDoesThisItemPopulated
} from '@bliks-io/contracts';
import { lookupAssignee, type AssigneeLookup } from '@bliks-io/ui';
import type {
  EntityUsageRole,
  TrackedUser,
  TrackedTeam,
  TrackedSystem,
  TrackedArtifact
} from '../ProcessDetailPage';

export interface AggregatedEntities {
  totalSteps: number;
  involvedPeople: TrackedUser[];
  /** OrgFunction assignees of `kind: 'team'`. */
  involvedTeams: TrackedTeam[];
  /** OrgFunction assignees of `kind: 'role'` (Funktionen). */
  involvedFunctions: TrackedTeam[];
  involvedSystems: TrackedSystem[];
  involvedArtifacts: TrackedArtifact[];
  involvedExternalContacts: PopulatedExternalContact[];
}

/**
 * Aggregates all involved entities from a process's step recordings.
 * Deduplicates by ID and tracks which steps each entity appears in.
 *
 * `assignedTo`, `helpers.systems`, `helpers.orgFunctions`, and
 * `whoElseDoesThis[].entity` arrive as unpopulated `{ type, id }` union refs —
 * they resolve against `lookup` (the same org-wide lookup every other read
 * surface uses); unresolvable refs
 * (suggestions, unknown ids) are skipped. The lookup opts into soft-deleted
 * entities, so resolved tombstones carry `deletedAt` and the entity cards
 * gray them out. Backend-populated users (recordedBy, helpers.persons) have
 * `deletedAt` stripped by the user serializer; `addPerson` re-stamps it from
 * the lookup.
 */
export function aggregateProcessEntities(
  process: ProcessWithSteps | undefined,
  t: (key: string) => string,
  lookup: AssigneeLookup
): AggregatedEntities {
  const steps = process?.processStepRecordings || [];
  const total = steps.length;

  // Maps for deduplication with usage tracking
  const peopleMap = new Map<string, TrackedUser>();
  // Both OrgFunction kinds (team + role) collect here; split by kind at return.
  const orgFunctionsMap = new Map<string, TrackedTeam>();
  const systemsMap = new Map<string, TrackedSystem>();
  const artifactsMap = new Map<string, TrackedArtifact>();
  const externalContactsMap = new Map<string, PopulatedExternalContact>();

  const addPerson = (
    user: PopulatedUser | undefined | null,
    stepName: string,
    usageRole: EntityUsageRole
  ) => {
    if (!user?._id) return;
    const existing = peopleMap.get(user._id);
    if (existing) {
      existing.usages.push({ stepName, role: usageRole });
    } else {
      // Backend-populated users never carry the soft-delete tombstone (the
      // user serializer strips it) — re-stamp the lifecycle from the lookup
      // so the UserCard grays deleted people out and suffixes the status.
      const lookupUser = lookup.users?.get(user._id);
      peopleMap.set(user._id, {
        ...user,
        deletedAt: lookupUser?.deletedAt ?? user.deletedAt,
        status: lookupUser?.status ?? user.status,
        usages: [{ stepName, role: usageRole }]
      });
    }
  };

  const addOrgFunction = (
    orgFunction: PopulatedOrgFunction,
    stepName: string,
    usageRole: EntityUsageRole = 'helper'
  ) => {
    const existing = orgFunctionsMap.get(orgFunction._id);
    if (existing) {
      existing.usages.push({ stepName, role: usageRole });
    } else {
      orgFunctionsMap.set(orgFunction._id, {
        ...orgFunction,
        usages: [{ stepName, role: usageRole }]
      });
    }
  };

  const addSystem = (
    system: PopulatedSystem,
    stepName: string,
    usageRole: EntityUsageRole = 'helper'
  ) => {
    const existing = systemsMap.get(system._id);
    if (existing) {
      existing.usages.push({ stepName, role: usageRole });
    } else {
      systemsMap.set(system._id, { ...system, usages: [{ stepName, role: usageRole }] });
    }
  };

  const addArtifact = (artifact: Artifact, stepName: string) => {
    const existing = artifactsMap.get(artifact._id);
    if (existing) {
      existing.usages.push({ stepName, role: 'helper' });
    } else {
      artifactsMap.set(artifact._id, { ...artifact, usages: [{ stepName, role: 'helper' }] });
    }
  };

  steps.forEach((step) => {
    const stepName =
      (step.actionType === 'decision' ? step.rules?.[0]?.output : step.task?.taskDescription) ||
      `${t('processes.stepNumber')}${step.stepNumber || '?'}`;

    if (step.recordedBy) {
      addPerson(step.recordedBy, stepName, 'recorded');
    }

    const assigned = lookupAssignee(step.assignedTo, step.assignedToModel, lookup);
    if (assigned) {
      switch (step.assignedToModel) {
        case 'User':
          addPerson(assigned as PopulatedUser, stepName, 'assigned');
          break;
        case 'OrgFunction':
          addOrgFunction(assigned as PopulatedOrgFunction, stepName, 'assigned');
          break;
        case 'System':
          addSystem(assigned as PopulatedSystem, stepName, 'assigned');
          break;
      }
    }

    step.whoElseDoesThis?.forEach((item: WhoElseDoesThisItemPopulated) => {
      if (item.status !== 'accepted') return;
      const entity = lookupAssignee(item.entity, item.model, lookup);
      if (!entity) return;
      if (item.model === 'User') {
        addPerson(entity as PopulatedUser, stepName, 'whoElseDoesThis');
      } else {
        addOrgFunction(entity as PopulatedOrgFunction, stepName, 'whoElseDoesThis');
      }
    });

    if (step.helpers?.persons) {
      step.helpers.persons.forEach((person: unknown) => {
        if (typeof person === 'object' && person !== null) {
          addPerson(person as PopulatedUser, stepName, 'helper');
        }
      });
    }

    if (step.helpers?.orgFunctions) {
      step.helpers.orgFunctions.forEach((ref: EntityOrSuggestionRef) => {
        const orgFunction = lookupAssignee(ref, 'OrgFunction', lookup);
        if (orgFunction) {
          addOrgFunction(orgFunction as PopulatedOrgFunction, stepName);
        }
      });
    }

    if (step.helpers?.systems) {
      step.helpers.systems.forEach((ref: EntityOrSuggestionRef) => {
        const system = lookupAssignee(ref, 'System', lookup);
        if (system) {
          addSystem(system as PopulatedSystem, stepName);
        }
      });
    }

    if (step.helpers?.documents) {
      step.helpers.documents.forEach((doc: unknown) => {
        if (typeof doc === 'object' && doc !== null && (doc as Artifact)._id) {
          addArtifact(doc as Artifact, stepName);
        }
      });
    }

    // A step keeps both subdocs across an actionType toggle — `actionType`
    // alone decides which branch's successors count as real participants.
    if (step.actionType === 'task' && step.task?.successors?.externalContacts) {
      step.task.successors.externalContacts.forEach((ec: unknown) => {
        if (typeof ec === 'object' && ec !== null && (ec as PopulatedExternalContact)._id) {
          const contact = ec as PopulatedExternalContact;
          if (!externalContactsMap.has(contact._id)) {
            externalContactsMap.set(contact._id, contact);
          }
        }
      });
    }

    if (step.actionType === 'decision') {
      step.rules?.forEach((rule: { successors?: { externalContacts?: unknown[] } }) => {
        if (rule.successors?.externalContacts) {
          rule.successors.externalContacts.forEach((ec: unknown) => {
            if (typeof ec === 'object' && ec !== null && (ec as PopulatedExternalContact)._id) {
              const contact = ec as PopulatedExternalContact;
              if (!externalContactsMap.has(contact._id)) {
                externalContactsMap.set(contact._id, contact);
              }
            }
          });
        }
      });
    }
  });

  // OrgFunctions are collected into one map, then split by `kind` for display:
  // Teams (`kind: 'team'`) and Funktionen (`kind: 'role'`) are separate surfaces.
  const orgFunctions = Array.from(orgFunctionsMap.values());

  return {
    totalSteps: total,
    involvedPeople: Array.from(peopleMap.values()),
    involvedTeams: orgFunctions.filter((of) => of.kind === 'team'),
    involvedFunctions: orgFunctions.filter((of) => of.kind === 'role'),
    involvedSystems: Array.from(systemsMap.values()),
    involvedArtifacts: Array.from(artifactsMap.values()),
    involvedExternalContacts: Array.from(externalContactsMap.values())
  };
}
